// src/components/FloorPlan3DModel.jsx
import React, { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useProgress, Html, useGLTF, Grid } from '@react-three/drei';

/**
 * Loader displays loading progress while the 3D model is being fetched.
 */
function Loader() {
  const { progress } = useProgress();
  return (
    <Html center>
      <div style={{ color: 'white', fontSize: 16, fontWeight: 'bold' }}>
        Loading... {Math.round(progress)}%
      </div>
    </Html>
  );
}

/**
 * DebugGrid renders a grid and axis helpers for debugging positioning.
 */
function DebugGrid() {
  return (
    <>
      {/* Grid on the ground plane */}
      <Grid 
        args={[20, 20]} 
        cellSize={1} 
        cellThickness={0.5} 
        cellColor={'#6f6f6f'} 
        sectionSize={5} 
        sectionThickness={1} 
        sectionColor={'#9d4b4b'} 
        fadeDistance={30} 
        fadeStrength={1} 
        followCamera={false} 
        infiniteGrid={true}
      />
      
      {/* Axis helpers - Red: X, Green: Y, Blue: Z */}
      <axesHelper args={[5]} />
      
      {/* Reference cubes at key positions */}
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="red" transparent opacity={0.3} />
      </mesh>
      
      <mesh position={[5, 0.5, 0]}>
        <boxGeometry args={[0.5, 0.5, 0.5]} />
        <meshStandardMaterial color="green" transparent opacity={0.3} />
      </mesh>
      
      <mesh position={[0, 0.5, 5]}>
        <boxGeometry args={[0.5, 0.5, 0.5]} />
        <meshStandardMaterial color="blue" transparent opacity={0.3} />
      </mesh>
    </>
  );
}
/**
 * MinimapModel loads a single GLTF model.
 */
function MinimapModel({ modelUrl }) {
  const { scene } = useGLTF(modelUrl);
  return <primitive object={scene} position={[0, 0, 0]} />;
}

/**
 * FloorPlan3DModel renders two models inside a canvas.
 */
function FloorPlan3DModel({ modelUrls = [], cameraTarget = [-5, 0, -1], showDebugGrid = false }) {
  return (
    <Canvas camera={{ position: [-20, 15, 10], fov: 50 }}>
      <ambientLight intensity={2} />
      <directionalLight position={[10, 10, 5]} intensity={2} />
      <OrbitControls target={cameraTarget} />
      
      {/* Debug grid - can be toggled on/off */}
      {showDebugGrid && <DebugGrid />}
      
      <Suspense fallback={<Loader />}>
        {modelUrls.map((url, index) => (
          <MinimapModel key={index} modelUrl={url} />
        ))}
      </Suspense>
    </Canvas>
  );
}

export default FloorPlan3DModel;